import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  CaretRight,
  ClipboardText,
  Blueprint,
  SidebarSimple,
  Plus,
  Gear,
  Selection,
  Users,
  UsersThree,
  Megaphone,
  Broadcast,
  MapTrifold,
  ChatCircle,
  ChartLineUp,
  ChartBar,
  ListChecks,
  ListBullets,
  Checks,
  Question,
} from '@phosphor-icons/react';
import { useOrganization } from '@/hooks/useQueries';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '@/components/ui/sidebar';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { useAuthStatus } from '@/hooks/useAuthStatus';
import { requestFirstRunTour } from '@/components/onboarding/FirstRunTour';
import { useImportForkVisibility } from '@/hooks/useImportForkVisibility';
import { Permission, maskHasPermission } from '@ruta/shared';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface NavTab {
  label: string;
  /** Value of the `?tab=` query param this sub-item navigates to. */
  tabValue: string;
  /** When true, the sub-tab is gated out for the current role and not rendered. */
  hidden?: boolean;
}

interface NavItem {
  label: string;
  to: string;
  icon: React.ReactNode;
  /** Sub-navigation tabs rendered as an expandable list under the item. */
  tabs?: NavTab[];
  /** The tab that is active when no `?tab=` param is present. */
  defaultTab?: string;
  /** When true, the item is gated out for the current role and not rendered. */
  hidden?: boolean;
  /**
   * Match the pathname exactly rather than by prefix. Needed when an item's path
   * is a prefix of a sibling's (e.g. /revenue vs /revenue/performance) so only
   * the precise page highlights.
   */
  exact?: boolean;
}

/** A titled cluster of nav items, grouped by the operator job it serves. */
interface NavSection {
  /** Small-caps section header. Omit for an unlabeled cluster. */
  label?: string;
  items: NavItem[];
}

interface SidebarNavProps {
  organizationData: any;
}

export function SidebarNav({ organizationData }: SidebarNavProps) {
  const location = useLocation();
  const { isMobile, setOpenMobile, toggleSidebar } = useSidebar();
  const navigate = useNavigate();

  // The guided tour, on demand: the same walk a new company gets. This button, the
  // "Show me around" card in Settings and the help menu all start it; it opens on the dashboard.
  const replayTour = () => {
    if (!orgId) return;
    requestFirstRunTour(orgId);
    if (isMobile) setOpenMobile(false);
    navigate('/assistant');
  };
  const { data: orgData } = useOrganization();
  const orgLogoUrl = orgData?.organization?.logoUrl;

  // Role gating: the sidebar should only surface what the signed-in role can
  // actually reach. Owner carries ALL_PERMISSIONS_MASK, so every item shows.
  const { permissionMask, orgId } = useAuthStatus();
  const has = (p: Permission) => maskHasPermission(permissionMask, p);
  const hasAny = (ps: Permission[]) => ps.some(has);
  const canManageRoles = has(Permission.ROLES_MANAGE);
  const { showTeam, showSubcontractorPay, showPayments } = useImportForkVisibility();

  // Tracks which expandable nav items the user has manually toggled.
  // When an item is absent here it falls back to "open while active".
  const [manualOpen, setManualOpen] = React.useState<Record<string, boolean>>({});

  // Close sidebar on mobile when navigation occurs
  const handleNavClick = (e: React.MouseEvent, to: string) => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  // Nav is organized by the operator job each cluster serves, most-frequent on
  // top, set-and-forget configuration pinned to the bottom. Each item is gated
  // to the permission(s) that make its destination reachable.
  const NAV_SECTIONS: NavSection[] = [
    {
      label: 'Operate',
      items: [
        // Live is the landing surface for everyone with app access — ungated.
        { label: 'Live', to: '/assistant', icon: <Broadcast className="h-4 w-4" weight="light" /> },
        {
          label: 'Map',
          to: '/map',
          icon: <MapTrifold className="h-4 w-4" weight="light" />,
          hidden: !has(Permission.MAP_VIEW),
        },
        {
          label: 'Schedule',
          to: '/schedule',
          icon: <ClipboardText className="h-4 w-4" weight="light" />,
          hidden: !hasAny([Permission.SCHEDULE_VIEW, Permission.VISITS_MANAGE]),
        },
        // Everything waiting on an office decision: visits that need a second
        // look before billing, and crew photos before a customer sees them.
        {
          label: 'Review',
          to: '/review',
          icon: <Checks className="h-4 w-4" weight="light" />,
          hidden: !hasAny([Permission.SCHEDULE_VIEW, Permission.VISITS_MANAGE]),
          defaultTab: 'visits',
          tabs: [
            { label: 'Visits', tabValue: 'visits' },
            // The photo wall reads and writes visit records directly (RUT-2455).
            { label: 'Photos', tabValue: 'photos', hidden: !has(Permission.VISITS_MANAGE) },
          ],
        },
      ],
    },
    {
      label: 'Grow',
      items: [
        {
          label: 'Messages',
          to: '/messages',
          icon: <ChatCircle className="h-4 w-4" weight="light" />,
          hidden: !hasAny([Permission.CUSTOMER_MESSAGES_VIEW, Permission.CREW_MESSAGES_VIEW]),
          defaultTab: 'customers',
          tabs: [
            { label: 'Customers', tabValue: 'customers', hidden: !has(Permission.CUSTOMER_MESSAGES_VIEW) },
            { label: 'Team', tabValue: 'team-chat', hidden: !has(Permission.CREW_MESSAGES_VIEW) },
          ],
        },
        {
          label: 'Campaigns',
          to: '/campaigns',
          icon: <Megaphone className="h-4 w-4" weight="light" />,
          hidden: !has(Permission.CAMPAIGNS_VIEW),
        },
      ],
    },
    {
      // Bulk operations over the full data sets — the big list/table views.
      label: 'Data',
      items: [
        {
          label: 'Customers',
          to: '/customers',
          icon: <Users className="h-4 w-4" weight="light" />,
          hidden: !has(Permission.CUSTOMERS_VIEW),
        },
        {
          label: 'Services',
          to: '/services',
          icon: <ListChecks className="h-4 w-4" weight="light" />,
          hidden: !has(Permission.SERVICES_VIEW),
          defaultTab: 'services',
          tabs: [
            { label: 'All Services', tabValue: 'services' },
            { label: 'Configuration', tabValue: 'config' },
            { label: 'Pricing', tabValue: 'pricing' },
          ],
        },
        {
          label: 'Visits',
          to: '/visits',
          exact: true,
          icon: <ListBullets className="h-4 w-4" weight="light" />,
          hidden: !hasAny([Permission.SCHEDULE_VIEW, Permission.VISITS_MANAGE]),
        },
      ],
    },
    {
      label: 'Insights',
      items: [
        {
          label: 'Revenue',
          to: '/revenue',
          exact: true,
          icon: <ChartLineUp className="h-4 w-4" weight="light" />,
          hidden: !has(Permission.REPORTS_VIEW),
        },
        {
          label: 'Performance',
          to: '/revenue/performance',
          icon: <ChartBar className="h-4 w-4" weight="light" />,
          hidden: !has(Permission.REPORTS_VIEW),
        },
        {
          label: 'Subcontractors',
          to: '/revenue/subcontractors',
          icon: <UsersThree className="h-4 w-4" weight="light" />,
          hidden: !has(Permission.REPORTS_VIEW) || !showSubcontractorPay,
        },
      ],
    },
    {
      label: 'Configure',
      items: [
        {
          label: 'Service Templates',
          to: '/service-templates',
          icon: <Blueprint className="h-4 w-4" weight="light" />,
          hidden: !has(Permission.SERVICE_TEMPLATES_VIEW),
        },
        {
          label: 'Service Area',
          to: '/service-area',
          icon: <Selection className="h-4 w-4" weight="light" />,
          hidden: !has(Permission.SERVICE_AREAS_VIEW),
        },
        {
          label: 'Team',
          to: '/team',
          icon: <UsersThree className="h-4 w-4" weight="light" />,
          hidden: !hasAny([Permission.TEAM_MANAGE, Permission.TEAM_INVITE, Permission.TEAM_CREWS]) || !showTeam,
          defaultTab: 'members',
          tabs: [
            { label: 'Members', tabValue: 'members' },
            { label: 'Crews', tabValue: 'crews' },
            { label: 'Subcontractors', tabValue: 'subcontractors', hidden: !showSubcontractorPay },
          ],
        },
        {
          label: 'Settings',
          to: '/settings',
          icon: <Gear className="h-4 w-4" weight="light" />,
          hidden: !hasAny([Permission.SETTINGS_VIEW, Permission.COMPANY_VIEW]),
          defaultTab: 'company',
          tabs: [
            { label: 'Company Profile', tabValue: 'company' },
            { label: 'Customer Profile', tabValue: 'portal' },
            ...(canManageRoles ? [{ label: 'Team & Roles', tabValue: 'team' }] : []),
            { label: 'Billing & Payments', tabValue: 'billing', hidden: !showPayments },
            { label: 'Notifications', tabValue: 'notifications' },
            { label: 'Field Operations', tabValue: 'field' },
            { label: 'Subscription', tabValue: 'subscription' },
            { label: 'Data & Import', tabValue: 'data' },
          ],
        },
      ],
    },
  ];

  // Drop gated-out items and sub-tabs, then drop any section left empty so no
  // stray header/divider renders for a role that can't see the cluster.
  const visibleSections = NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items
      .filter((item) => !item.hidden)
      .map((item) => ({
        ...item,
        tabs: item.tabs?.filter((tab) => !tab.hidden),
      })),
  })).filter((section) => section.items.length > 0);

  // Whether an item is the active nav entry. Some items share a base path
  // (Services, Service Config, and Pricing all live on /services, distinguished
  // by ?tab=), so matching is tab-aware: an expandable item owns its tab values
  // plus the no-param default; a plain item that pins a tab matches only that
  // tab; and `exact` items (e.g. /revenue vs /revenue/performance) match the
  // pathname exactly.
  const isActive = (item: NavItem) => {
    const [path, query] = item.to.split('?');
    if (path === '/') return location.pathname === '/';
    if (item.exact) return location.pathname === path;
    if (!location.pathname.startsWith(path)) return false;

    const currentTab = new URLSearchParams(location.search).get('tab');

    if (item.tabs && item.tabs.length > 0) {
      if (currentTab == null) return true;
      return item.tabs.some((t) => t.tabValue === currentTab);
    }

    const toTab = new URLSearchParams(query ?? '').get('tab');
    if (toTab != null) {
      // A pinned-tab item matches its exact tab, and also the no-param URL when
      // that tab is the page's default (so bare /services highlights Services,
      // not the Config/Pricing items that share the page).
      return currentTab === toTab || (currentTab == null && item.defaultTab === toTab);
    }

    return true;
  };

  // Resolve which tab is active for an expandable item, falling back to its default.
  const activeTabValue = (item: NavItem) => {
    const param = new URLSearchParams(location.search).get('tab');
    return param ?? item.defaultTab;
  };

  return (
    <Sidebar
      side="left"
      theme="navigation"
      collapsible="icon"
      size="narrow"
    >
      <SidebarHeader>
        {/* Logo */}
        <SidebarGroup className="p-0">
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <Link
                  to="/"
                  className="flex w-full cursor-pointer items-center justify-center px-2 py-1 group-data-[collapsible=icon]:px-0 group-data-[collapsible=icon]:py-2"
                >
                  <div className="flex h-full w-full items-center justify-center">
                    {/* Full brand when expanded — RUTA + org mark on one row.

                        Sits level with the page greeting across the rail (RUT-3379).
                        SidebarHeader's p-2 plus this row's py-1 lands the lockup's
                        centre on the centre of PageHeader's title+subtitle block
                        (its own py-2 plus ~50px of text), so the two read as one dark
                        band instead of two things at different heights. This is a
                        fixed-offset match, not an invariant. Two things move the
                        greeting without moving the rail, because ViewAsBanner and
                        ProvisioningBanner sit above PageHeader inside SidebarInset
                        (App.tsx) while the rail is anchored to the top of the layout:
                        ViewAsBanner shows only during operator impersonation, and
                        ProvisioningBanner shows to COMPANY_MANAGE holders taking cards
                        until payments actually work (it clears only on approved/enabled,
                        stays up indefinitely on rejected, and yields to the setup card on
                        Live during setup) — so a newly onboarded owner sees the
                        lockup sit high until their application goes through.

                        It is also tuned to this header's shape, a title with a
                        subtitle, whose centre lands at ~33px against the lockup's ~34px.
                        PageHeader's other shapes put theirs elsewhere: with no subtitle
                        min-h-9 floors the block and its centre rises to ~26px, and a
                        breadcrumbed header is further out still — today's only one is
                        titleless (the staff customer page), where the crumb row centres
                        at ~22px and the lockup reads about 12px below it. Those are
                        different headers, not regressions. Nothing binds the two, so
                        changing PageHeader's padding or title type un-levels this rail.

                        Horizontal inset is px-2 so the content sits 16px in, matching
                        the nav items below (SidebarHeader's p-2 plus this px-2, against
                        SidebarGroup's p-2 plus SidebarMenuButton's p-2).

                        Sizing is a width trade, not a free bump. The rail is only
                        14rem, and RUTA is shrink-0 while the logo is min-w-0 — so
                        every pixel the wordmark takes comes straight out of the logo.
                        text-4xl is paid for by the tighter inset above and the smaller
                        separator below; measured off real renders (painted ink, +/-1px)
                        against the old text-3xl/h-9 pair, an oval wordmark goes
                        ~60x31 -> ~64x34 and an extreme 4.5:1 banner ~61x14 -> ~66x14
                        (width only — it is already
                        width-bound). h-11 is the cap for tall/square marks; a wide
                        one is width-bound long before it reaches h-11 or max-w-[7.5rem],
                        so do not grow RUTA further without widening the rail. */}
                    <div className="group-data-[collapsible=icon]:hidden flex w-full max-w-full flex-row items-center justify-center gap-1.5 text-4xl">
                      <h1 className="shrink-0 font-prompt font-black tracking-wider text-white">
                        RUTA
                      </h1>
                      {orgLogoUrl ? (
                        <>
                          <Plus className="h-2.5 w-2.5 shrink-0 text-white" weight="light" />
                          <img
                            src={orgLogoUrl}
                            alt={organizationData?.name || 'Organization'}
                            className="h-11 min-w-0 w-auto max-w-[7.5rem] object-contain"
                          />
                        </>
                      ) : organizationData?.name ? (
                        <>
                          <Plus className="h-2.5 w-2.5 shrink-0 text-white" weight="light" />
                          <div className="min-w-0 truncate text-sm uppercase text-white">
                            {organizationData.name}
                          </div>
                        </>
                      ) : null}
                    </div>
                    {/* R logo when collapsed */}
                    <div className="hidden group-data-[collapsible=icon]:flex h-8 w-8 items-center justify-center rounded-lg">
                      <span className="font-prompt text-lg font-black text-white">R</span>
                    </div>
                  </div>
                </Link>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

      </SidebarHeader>

      <SidebarContent>
        {visibleSections.map((section, sectionIdx) => (
          <React.Fragment key={section.label ?? sectionIdx}>
            {/* No dividers between sections — the small-caps labels alone group
                the items, which reclaims vertical space. */}
            <SidebarGroup className="py-1">
              {section.label && (
                <SidebarGroupLabel className="h-6 px-2 text-[10px] font-semibold uppercase tracking-wider text-white/40">
                  {section.label}
                </SidebarGroupLabel>
              )}
              <SidebarGroupContent>
                <SidebarMenu>
                  {section.items.map((item) => {
                    const active = isActive(item);

                    // Plain item without sub-tabs.
                    if (!item.tabs || item.tabs.length === 0) {
                      return (
                        <SidebarMenuItem key={item.label}>
                          <SidebarMenuButton asChild isActive={active} className="h-8 text-sm [&>svg]:!size-4">
                            <Link to={item.to} onClick={(e) => handleNavClick(e, item.to)} data-tour={`nav${item.to.replace(/\//g, '-')}`}>
                              {item.icon}
                              <span>{item.label}</span>
                            </Link>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      );
                    }

                    // Collapsed by default; opens while active or when the user
                    // manually toggles it.
                    const isOpen = manualOpen[item.label] ?? active;
                    const currentTab = activeTabValue(item);

                    return (
                      <Collapsible
                        key={item.label}
                        asChild
                        open={isOpen}
                        onOpenChange={(open) =>
                          setManualOpen((prev) => ({ ...prev, [item.label]: open }))
                        }
                      >
                        <SidebarMenuItem>
                          <SidebarMenuButton asChild isActive={active} className="h-8 text-sm [&>svg]:!size-4">
                            <Link
                              to={item.to}
                              onClick={(e) => {
                                handleNavClick(e, item.to);
                                setManualOpen((prev) => ({ ...prev, [item.label]: true }));
                              }}
                              data-tour={`nav${item.to.replace(/\//g, '-')}`}
                            >
                              {item.icon}
                              <span>{item.label}</span>
                            </Link>
                          </SidebarMenuButton>
                          <CollapsibleTrigger asChild>
                            <SidebarMenuAction className="right-2 text-white/40 transition-transform duration-200 peer-data-[active=true]/menu-button:text-sidebar-accent-foreground data-[state=open]:rotate-90 [&>svg]:!size-3.5">
                              <CaretRight weight="bold" />
                              <span className="sr-only">Toggle {item.label}</span>
                            </SidebarMenuAction>
                          </CollapsibleTrigger>
                          <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
                            <SidebarMenuSub className="ml-4 mr-0 gap-0.5 border-white/10 py-1 pl-3 pr-0">
                              {item.tabs.map((tab) => {
                                const tabHref = `${item.to}?tab=${tab.tabValue}`;
                                const tabActive = active && currentTab === tab.tabValue;
                                return (
                                  <SidebarMenuSubItem key={tab.tabValue}>
                                    <SidebarMenuSubButton
                                      asChild
                                      isActive={tabActive}
                                      className="text-white/55 data-[active=true]:font-medium"
                                    >
                                      <Link to={tabHref} onClick={(e) => handleNavClick(e, tabHref)}>
                                        <span>{tab.label}</span>
                                      </Link>
                                    </SidebarMenuSubButton>
                                  </SidebarMenuSubItem>
                                );
                              })}
                            </SidebarMenuSub>
                          </CollapsibleContent>
                        </SidebarMenuItem>
                      </Collapsible>
                    );
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </React.Fragment>
        ))}
      </SidebarContent>

      <SidebarFooter className="p-2 pb-0 gap-2">
        {/* Expanded: version paired with a subtle collapse toggle */}
        <div className="group-data-[collapsible=icon]:hidden flex items-center justify-between gap-2 px-3 pb-1.5">
          <span className="text-[11px] font-medium tabular-nums text-white/80">
            v{import.meta.env.VITE_APP_VERSION}
          </span>
          <div className="flex items-center gap-0.5">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={replayTour}
                  disabled={!orgId}
                  aria-label="Show me around"
                  className="p-1 rounded text-white/35 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-40"
                >
                  <Question className="h-4 w-4 flex-shrink-0" weight="light" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">Show me around — a walk through every page, a few minutes</TooltipContent>
            </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={toggleSidebar}
                aria-label="Collapse navigation to icons"
                className="p-1 rounded text-white/35 hover:text-white hover:bg-white/10 transition-colors"
              >
                <SidebarSimple className="h-4 w-4 flex-shrink-0" weight="light" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right">Collapse navigation to icons</TooltipContent>
          </Tooltip>
          </div>
        </div>

        {/* Collapsed: the tour, then the expand toggle */}
        <div className="hidden group-data-[collapsible=icon]:flex flex-col items-center gap-1 pb-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <SidebarMenuButton onClick={replayTour} disabled={!orgId} asChild>
                <button aria-label="Show me around" className="flex items-center justify-center w-full hover:!bg-white/10 hover:!text-white">
                  <Question className="h-4 w-4 flex-shrink-0" weight="light" />
                </button>
              </SidebarMenuButton>
            </TooltipTrigger>
            <TooltipContent side="right">Show me around</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <SidebarMenuButton onClick={toggleSidebar} asChild>
                <button className="flex items-center justify-center w-full hover:!bg-white/10 hover:!text-white">
                  <SidebarSimple className="h-4 w-4 flex-shrink-0" weight="light" mirrored />
                </button>
              </SidebarMenuButton>
            </TooltipTrigger>
            <TooltipContent side="right">Expand navigation</TooltipContent>
          </Tooltip>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}